import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * What a card is for, shown by the colour of the icon tile in its title, so
 * the same kind of card looks the same on every page:
 * - `details`: what the object is, its name and core settings (default)
 * - `money`: budgets, pricing, fees, billing and bidding
 * - `delivery`: where and how it runs, such as targeting, sites and capping
 * - `people`: users, teams, roles, access and contact details
 * - `creative`: ad content, assets, layouts, templates and previews
 * - `attention`: rules to comply with, such as political ads and blocklists
 * - `insight`: read-only history, activity, statistics and reports
 * - `danger`: errors and things that went wrong
 */
type CardTone =
  | "details"
  | "money"
  | "delivery"
  | "people"
  | "creative"
  | "attention"
  | "insight"
  | "danger"

const CARD_TONES: Record<CardTone, string> = {
  details: "[--card-tone:var(--ad-link)] [--card-tone-soft:var(--ad-primary-soft)]",
  money: "[--card-tone:var(--ad-icon-green)] [--card-tone-soft:var(--ad-icon-green-bg)]",
  delivery: "[--card-tone:var(--ad-icon-purple)] [--card-tone-soft:var(--ad-icon-purple-bg)]",
  people: "[--card-tone:var(--ad-icon-teal)] [--card-tone-soft:var(--ad-icon-teal-bg)]",
  creative: "[--card-tone:var(--ad-icon-pink)] [--card-tone-soft:var(--ad-icon-pink-bg)]",
  attention: "[--card-tone:var(--ad-warning)] [--card-tone-soft:var(--ad-warning-soft)]",
  insight: "[--card-tone:var(--ad-text-muted)] [--card-tone-soft:var(--ad-neutral-soft)]",
  danger: "[--card-tone:var(--ad-danger)] [--card-tone-soft:var(--ad-danger-soft)]",
}

/**
 * How the card sits on the page:
 * - `raised`: a bordered card off the page, for anything you fill in or act on (default)
 * - `sunken`: borderless and recessed into the page, for read-only figures and history
 * - `highlight`: a blue header band, for the read-only overview of the object a page is about
 */
type CardSurface = "raised" | "sunken" | "highlight"

function Card({
  className,
  size = "default",
  tone = "details",
  surface = "raised",
  ...props
}: React.ComponentProps<"div"> & {
  size?: "default" | "sm"
  tone?: CardTone
  surface?: CardSurface
}) {
  return (
    <div
      data-slot="card"
      data-size={size}
      data-tone={tone}
      data-surface={surface}
      className={cn(
        CARD_TONES[tone],
        "group/card flex flex-col gap-(--card-spacing) overflow-hidden rounded-xl bg-card py-(--card-spacing) text-sm text-card-foreground shadow-xs ring-1 ring-foreground/10 [--card-spacing:--spacing(6)] has-[>img:first-child]:pt-0 data-[size=sm]:[--card-spacing:--spacing(4)] *:[img:first-child]:rounded-t-xl *:[img:last-child]:rounded-b-xl",
        surface === "sunken" && "bg-(--ad-surface-sunken) shadow-none ring-foreground/5",
        // Only the header is tinted: a band ruled off from the body, padded like
        // any other card so its title lines up with the cards beside it.
        surface === "highlight" &&
          "pt-0 ring-(--ad-card-highlight-border) *:data-[slot=card-header]:border-b *:data-[slot=card-header]:border-(--ad-card-highlight-border) *:data-[slot=card-header]:bg-(--ad-card-highlight-bg) *:data-[slot=card-header]:py-(--card-spacing)",
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "group/card-header @container/card-header grid auto-rows-min items-start gap-1 rounded-t-xl px-(--card-spacing) has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-(--card-spacing)",
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        "flex items-center gap-3 font-heading text-base leading-normal font-medium group-data-[size=sm]/card:text-sm",
        className
      )}
      {...props}
    />
  )
}

/** The icon before a card's name, in a square tile coloured by the card's `tone`. */
function CardIcon({
  icon: Icon,
  className,
}: {
  icon: React.ComponentType<{ className?: string }>
  className?: string
}) {
  return (
    <span
      data-slot="card-icon"
      aria-hidden="true"
      className={cn(
        "grid size-8 shrink-0 place-items-center rounded-md bg-(--card-tone-soft) text-(--card-tone)",
        className
      )}
    >
      <Icon className="size-4" />
    </span>
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("flex flex-col gap-3 px-(--card-spacing)", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "flex items-center rounded-b-xl px-(--card-spacing) [.border-t]:pt-(--card-spacing)",
        className
      )}
      {...props}
    />
  )
}

export type { CardTone, CardSurface }

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardIcon,
  CardAction,
  CardDescription,
  CardContent,
}
